import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { BookingEmbed } from '../components/sections/BookingEmbed';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, BrainCircuitIcon, CpuIcon, DatabaseIcon, CodeIcon, NetworkIcon, LightbulbIcon, TrendingUpIcon, UsersIcon, CalendarIcon, DollarSignIcon, ClipboardCheckIcon, BookOpenIcon, UserIcon, BedIcon, UtensilsIcon, MusicIcon, PartyPopperIcon, HeartIcon, GlassesIcon, ShoppingBagIcon, CompassIcon, MapPinIcon, VideoIcon, TargetIcon, ShieldIcon, ZapIcon, TrophyIcon, MessageCircleIcon, BuildingIcon, GraduationCapIcon } from 'lucide-react';
export function AILeadersRetreat() {
  const [activePersona, setActivePersona] = useState('caio');
  const [activeAgendaDay, setActiveAgendaDay] = useState(0);
  const [activeEventCategory, setActiveEventCategory] = useState(0);
  const eventCategories = [{
    label: 'AI STRATEGY',
    tagline: 'Vision-Setting',
    color: 'from-[#0099A1] to-[#006D73]',
    icon: <BrainCircuitIcon className="h-5 w-5" data-id="element-699" />,
    description: 'Align the organization on AI adoption and governance.',
    events: [{
      name: 'AI Strategy Summit',
      detail: 'Define the 12-month roadmap for GenAI adoption across the enterprise'
    }, {
      name: 'Governance & Ethics Sprint',
      detail: 'Establish guardrails, compliance frameworks, and responsible AI principles'
    }, {
      name: 'Executive AI Alignment',
      detail: 'Educate and align the C-Suite on realistic AI capabilities and investments'
    }, {
      name: 'Build vs. Buy Workshop',
      detail: 'Critical decision-making framework for infrastructure and tooling'
    }]
  }, {
    label: 'INNOVATION & BUILD',
    tagline: 'High-Velocity',
    color: 'from-[#FF6B65] to-[#E05550]',
    icon: <CodeIcon className="h-5 w-5" data-id="element-700" />,
    description: 'Accelerate prototyping and proof-of-concept delivery.',
    events: [{
      name: 'Executive Hackathon',
      detail: 'High-impact prototyping session for senior technical leaders'
    }, {
      name: 'Product Innovation Sprint',
      detail: 'Joint session with Product & Engineering to map AI-native features'
    }, {
      name: 'Data Architecture Offsite',
      detail: 'Deep dive into modern data stack, vector DBs, and pipeline optimization'
    }, {
      name: 'R&D Moonshot Lab',
      detail: 'Dedicated time to explore edge cases and next-gen model capabilities'
    }]
  }, {
    label: 'TALENT & CULTURE',
    tagline: 'Team-Building',
    color: 'from-[#D4A853] to-[#B8922E]',
    icon: <UsersIcon className="h-5 w-5" data-id="element-701" />,
    description: 'Attract, retain, and upskill top technical talent.',
    events: [{
      name: 'AI Leadership Offsite',
      detail: 'Aligning VP/Director level on hiring, culture, and retention strategies'
    }, {
      name: 'Research Retreat',
      detail: 'Reward top researchers/engineers with deep-work time in a luxury setting'
    }, {
      name: 'Cross-Functional Bridge',
      detail: 'Uniting Data Science, Engineering, and Product teams to break silos'
    }]
  }, {
    label: 'EXTERNAL & ECOSYSTEM',
    tagline: 'Market-Facing',
    color: 'from-[#374047] to-[#2A3035]',
    icon: <NetworkIcon className="h-5 w-5" data-id="element-702" />,
    description: 'Engage with partners, customers, and the broader ecosystem.',
    events: [{
      name: 'AI Customer Advisory Board',
      detail: 'Gather feedback on AI features from your most strategic customers'
    }, {
      name: 'Partner Ecosystem Summit',
      detail: 'Align with cloud providers, model partners, and integrators'
    }, {
      name: 'Developer Relations Retreat',
      detail: 'Strategy session for building and nurturing your developer community'
    }]
  }, {
    label: 'OPERATIONS & SCALE',
    tagline: 'Foundation-Building',
    color: 'from-[#0099A1] to-[#374047]',
    icon: <DatabaseIcon className="h-5 w-5" data-id="element-703" />,
    description: 'Scale your AI operations from pilot to production.',
    events: [{
      name: 'LLMOps & MLOps Summit',
      detail: 'Standardizing deployment, monitoring, and evaluation pipelines'
    }, {
      name: 'Cost Optimization Sprint',
      detail: 'Strategies to manage inference costs and compute budget efficiency'
    }, {
      name: 'Security & Compliance Deep Dive',
      detail: 'Hardening systems against prompt injection and data leakage'
    }]
  }];
  const personas = [{
    id: 'caio',
    title: 'Chief AI Officers',
    roles: 'CAIO / VP of AI / Head of AI',
    icon: <BrainCircuitIcon className="h-6 w-6 text-[#0099A1]" data-id="element-704" />,
    hook: 'Design your enterprise AI roadmap in 48 hours—away from the noise.',
    value: 'Build a board-ready AI strategy and governance framework.'
  }, {
    id: 'data',
    title: 'Data Leaders',
    roles: 'CDO / VP Data / Head of Data Science',
    icon: <DatabaseIcon className="h-6 w-6 text-[#0099A1]" data-id="element-705" />,
    hook: 'Architect your modern data stack for the GenAI era.',
    value: 'Solve data quality, lineage, and access challenges to unlock AI velocity.'
  }, {
    id: 'product',
    title: 'AI Product Leaders',
    roles: 'CPO / VP Product (AI) / Head of AI Product',
    icon: <LightbulbIcon className="h-6 w-6 text-[#0099A1]" data-id="element-706" />,
    hook: 'Map the next generation of AI-native features and experiences.',
    value: 'Align product vision with technical feasibility and market needs.'
  }, {
    id: 'engineering',
    title: 'Engineering Leaders',
    roles: 'CTO / VP Engineering / Head of ML Infra',
    icon: <CpuIcon className="h-6 w-6 text-[#0099A1]" data-id="element-707" />,
    hook: 'Solve the "Build vs. Buy" dilemma and scale your MLOps.',
    value: 'Define the technical architecture that scales from pilot to production.'
  }, {
    id: 'innovation',
    title: 'Innovation Heads',
    roles: 'Head of Innovation / R&D Directors',
    icon: <ZapIcon className="h-6 w-6 text-[#0099A1]" data-id="element-708" />,
    hook: 'Turn R&D experiments into deployable business value.',
    value: 'Bridge the gap between research labs and real-world ROI.'
  }, {
    id: 'security',
    title: 'AI Security & Risk',
    roles: 'CISO / AI Risk Officer / Compliance',
    icon: <ShieldIcon className="h-6 w-6 text-[#0099A1]" data-id="element-709" />,
    hook: 'Establish the guardrails that make AI safe for the enterprise.',
    value: 'Create comprehensive frameworks for responsible AI and data privacy.'
  }];
  const valuePropositions = {
    caio: {
      title: 'Chief AI Officers',
      subtitle: 'CAIO / VP of AI / Head of AI',
      headline: 'Build a board-ready AI strategy in 48 hours.',
      supportingLine: 'Prototype your next AI Strategy Summit or Executive Offsite with measurable outcomes: adoption roadmap, governance framework, and ROI modeling.',
      points: ['Ready-to-run agenda + facilitation plan (AI Summit / Strategy Offsite)', 'AI ROI model + adoption dashboard (use case velocity + value realization)', 'Governance framework template (risk tiers, approval flows, ethical guidelines)', 'Peer working sessions with top AI leaders (benchmark your stack and strategy)'],
      riskRemover: 'Not a vendor pitch. Approval-friendly package available (official letter, objectives, value disclosure).',
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop'
    },
    data: {
      title: 'Data Leaders',
      subtitle: 'CDO / VP Data / Head of Data Science',
      points: ['Data readiness assessment framework for GenAI', 'Unstructured data strategy playbook', 'Vector database and retrieval architecture blueprints', 'Data governance templates for LLM training/fine-tuning'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop'
    },
    product: {
      title: 'AI Product Leaders',
      subtitle: 'CPO / VP Product (AI) / Head of AI Product',
      points: ['AI-native product roadmap template', 'UX/UI patterns for generative interfaces', 'Evaluation framework for model performance vs. user experience', 'Pricing and packaging models for AI features'],
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop'
    },
    engineering: {
      title: 'Engineering Leaders',
      subtitle: 'CTO / VP Engineering / Head of ML Infra',
      points: ['LLMOps reference architecture and toolchain selection', 'Cost optimization strategies for inference at scale', 'Evaluation pipeline design (auto-evals, human-in-the-loop)', 'Latency and reliability SLAs for production AI'],
      image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070&auto=format&fit=crop'
    },
    innovation: {
      title: 'Innovation Heads',
      subtitle: 'Head of Innovation / R&D Directors',
      points: ['Proof-of-Concept to Production graduation framework', 'Internal hackathon playbook that drives real value', 'Emerging tech radar and vendor assessment scoring', 'Build vs. Buy decision matrix for new capabilities'],
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2070&auto=format&fit=crop'
    },
    security: {
      title: 'AI Security & Risk',
      subtitle: 'CISO / AI Risk Officer / Compliance',
      points: ['AI Red Teaming playbook and vulnerability assessment', 'Data leakage prevention strategies for LLMs', 'Compliance checklist for EU AI Act and local regulations', 'Vendor risk assessment template for AI tools'],
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop'
    }
  };
  const agendaItems = [{
    day: 'Day 1',
    timeframe: 'Afternoon & Evening',
    items: [{
      time: '2:00 PM',
      title: 'VIP Arrivals & Welcome',
      description: 'Private check-in experience with personalized welcome'
    }, {
      time: '3:30 PM',
      title: 'State of AI Masterclass',
      description: '90-minute deep dive on the latest models, agents, and enterprise adoption trends'
    }, {
      time: '6:00 PM',
      title: 'Immersive Welcome Reception',
      description: 'Networking with fellow AI leaders and showcase of destination experiences'
    }, {
      time: '7:30 PM',
      title: 'Curated Dining Experience',
      description: 'Signature culinary journey with facilitated discussions on AI ethics and future'
    }, {
      time: '9:00 PM',
      title: 'Live Entertainment Experience',
      description: '6-star resort live entertainment in a refined luxury setting'
    }]
  }, {
    day: 'Day 2',
    timeframe: 'Full Day Experience',
    items: [{
      time: '7:00 AM',
      title: 'Optional Wellness Activity',
      description: 'Sunrise yoga or guided beach meditation to clear the mind'
    }, {
      time: '8:30 AM',
      title: 'Breakfast & Track Selection',
      description: 'Choose your focused learning track (Strategy, Build, or Governance)'
    }, {
      time: '9:30 AM',
      title: 'Specialized Tracks',
      description: 'AI Strategy Sprint / LLMOps Deep Dive / Governance Workshop (3 hours)'
    }, {
      time: '12:30 PM',
      title: 'Collaborative Lunch',
      description: 'Shared insights and cross-track pollination'
    }, {
      time: '2:00 PM',
      title: 'Site Tours & Experiential Showcase',
      description: 'Curated exploration of venue capabilities for your next offsite'
    }, {
      time: '4:00 PM',
      title: 'Design Lab',
      description: 'Facilitated workshop to apply learnings to your specific organizational roadmap'
    }, {
      time: '7:00 PM',
      title: 'Signature Networking Experience',
      description: 'Immersive evening with local cultural elements'
    }, {
      time: '9:30 PM',
      title: 'Themed Party Night',
      description: 'Showcasing destination-level production and atmosphere'
    }]
  }, {
    day: 'Day 3',
    timeframe: 'Morning',
    items: [{
      time: '8:00 AM',
      title: 'Breakfast & Reflection',
      description: 'Guided discussion on key takeaways and implementation steps'
    }, {
      time: '9:00 AM',
      title: 'Playbook Finalization',
      description: 'Complete your actionable AI framework with expert guidance'
    }, {
      time: '10:30 AM',
      title: 'One-to-One Consultations',
      description: 'Personalized next steps with our specialists'
    }, {
      time: '12:00 PM',
      title: 'Farewell Lunch & Departures',
      description: 'Closing remarks and continuation planning'
    }]
  }];
  const resortActivities = [{
    title: 'Live Music & Concerts',
    icon: <MusicIcon className="h-8 w-8 text-white" data-id="element-710" />,
    description: 'Enjoy nightly concerts featuring top tribute bands and local talent on multiple stages.'
  }, {
    title: 'Legendary Pool Parties',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-711" />,
    description: 'Experience high-energy pool parties featuring DJs and curated poolside entertainment.'
  }, {
    title: 'Themed Nights & Shows',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-712" />,
    description: 'Participate in themed dinner shows, fiestas, and professional productions.'
  }, {
    title: 'Luxury Spa Treatments',
    icon: <HeartIcon className="h-8 w-8 text-white" data-id="element-713" />,
    description: 'Unwind with signature wellness treatments, massages, and rejuvenating therapies.'
  }, {
    title: 'Premium Amenities',
    icon: <GlassesIcon className="h-8 w-8 text-white" data-id="element-714" />,
    description: 'Access exclusive resort amenities, premium facilities, and personalized services.'
  }, {
    title: 'VIP Access Benefits',
    icon: <ShoppingBagIcon className="h-8 w-8 text-white" data-id="element-715" />,
    description: 'Enjoy exclusive access to premium amenities, priority reservations, and concierge service.'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-716">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden min-h-[600px]" data-id="element-717">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-718">
          <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop" alt="AI Leaders Retreat" className="w-full h-full object-cover scale-105" data-id="element-719" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" data-id="element-720"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-721">
          <div className="max-w-3xl" data-id="element-722">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/30" data-id="element-723">
              <BrainCircuitIcon className="h-4 w-4 mr-2" data-id="element-724" />
              AI Leaders Retreat • Capped at 20 Executives
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 [text-shadow:_0_2px_20px_rgb(0_0_0_/_80%)]" data-id="element-725">
              Your Complimentary AI Leadership Retreat
            </h1>
            <p className="text-xl md:text-2xl text-white mb-8 [text-shadow:_0_2px_16px_rgb(0_0_0_/_70%)] leading-relaxed" data-id="element-726">
              Define Your Enterprise AI Strategy in 48 Hours With Elite Peers
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8" data-id="element-727">
              <Button variant="primary" size="lg" to="/contact" data-id="element-728">
                Check Eligibility
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20" data-id="element-729">
                15-Min Fit Check
              </Button>
            </div>
            <p className="text-sm text-white/80 mt-4 [text-shadow:_0_2px_12px_rgb(0_0_0_/_60%)]" data-id="element-730">
              Approval-safe • Compliance documentation provided • No vendor
              pitches
            </p>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 bg-[#374047]" data-id="element-731">
        <div className="container mx-auto px-4 md:px-6" data-id="element-732">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/90 text-sm" data-id="element-733">
            <div className="flex items-center gap-2" data-id="element-734">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-735" />
              <span data-id="element-736">Top AI Researchers/Faculty</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-737">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-738" />
              <span data-id="element-739">Peer-Level Cohorts Only</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-740">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-741" />
              <span data-id="element-742">Tangible Strategy Roadmap</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-743">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-744" />
              <span data-id="element-745">Compliance-Ready Package</span>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Leave With */}
      <section className="py-16 bg-white" data-id="element-746">
        <div className="container mx-auto px-4 md:px-6" data-id="element-747">
          <div className="max-w-4xl mx-auto" data-id="element-748">
            <div className="text-center mb-12" data-id="element-749">
              <div className="inline-flex items-center px-4 py-2 bg-[#0099A1]/10 rounded-full text-[#0099A1] text-sm font-medium mb-4" data-id="element-750">
                <ClipboardCheckIcon className="h-4 w-4 mr-2" data-id="element-751" />
                Tangible Deliverables
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-752">
                Your AI Strategy Toolkit
              </h2>
              <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-753">
                Leave with everything you need to plan, budget, and execute your
                enterprise AI roadmap
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-id="element-754">
              <div className="group relative bg-gradient-to-br from-[#0099A1]/5 to-[#0099A1]/10 rounded-2xl p-8 border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all duration-300" data-id="element-755">
                <div className="absolute top-6 right-6 w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] font-bold text-xl" data-id="element-756">
                  1
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-757">
                  <CompassIcon className="h-8 w-8 text-white" data-id="element-758" />
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-759">
                  Adoption Roadmap & Budget
                </h3>
                <p className="text-[#374047]/70 leading-relaxed" data-id="element-760">
                  Detailed 12-month implementation plan with milestone mapping,
                  resource requirements, and budget estimates for
                  pilot-to-production.
                </p>
                <div className="mt-6 pt-6 border-t border-[#0099A1]/20" data-id="element-761">
                  <div className="flex items-center text-sm text-[#0099A1] font-medium" data-id="element-762">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-763" />
                    <span data-id="element-764">Board-ready presentation</span>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-[#0099A1]/5 to-[#0099A1]/10 rounded-2xl p-8 border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all duration-300" data-id="element-765">
                <div className="absolute top-6 right-6 w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] font-bold text-xl" data-id="element-766">
                  2
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-767">
                  <ShieldIcon className="h-8 w-8 text-white" data-id="element-768" />
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-769">
                  Governance & Risk Framework
                </h3>
                <p className="text-[#374047]/70 leading-relaxed" data-id="element-770">
                  Comprehensive policy templates for responsible AI, data
                  privacy, vendor assessment, and internal usage guardrails.
                </p>
                <div className="mt-6 pt-6 border-t border-[#0099A1]/20" data-id="element-771">
                  <div className="flex items-center text-sm text-[#0099A1] font-medium" data-id="element-772">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-773" />
                    <span data-id="element-774">Compliance-ready policies</span>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-[#0099A1]/5 to-[#0099A1]/10 rounded-2xl p-8 border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all duration-300" data-id="element-775">
                <div className="absolute top-6 right-6 w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] font-bold text-xl" data-id="element-776">
                  3
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-777">
                  <TrendingUpIcon className="h-8 w-8 text-white" data-id="element-778" />
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-779">
                  ROI & Value Model
                </h3>
                <p className="text-[#374047]/70 leading-relaxed" data-id="element-780">
                  Framework for measuring AI impact, including productivity
                  metrics, revenue influence, and cost-saving calculations.
                </p>
                <div className="mt-6 pt-6 border-t border-[#0099A1]/20" data-id="element-781">
                  <div className="flex items-center text-sm text-[#0099A1] font-medium" data-id="element-782">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-783" />
                    <span data-id="element-784">Defensible business case</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center" data-id="element-785">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#F4E8D0]/50 rounded-full" data-id="element-786">
                <TrendingUpIcon className="h-5 w-5 text-[#0099A1]" data-id="element-787" />
                <span className="text-[#374047] font-medium" data-id="element-788">
                  Work product valued at $25,000+ in consulting fees
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Persona Selector */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-789">
        <div className="container mx-auto px-4 md:px-6" data-id="element-790">
          <div className="max-w-4xl mx-auto text-center mb-12" data-id="element-791">
            <div className="inline-flex items-center px-4 py-2 bg-white rounded-full text-[#374047] text-sm font-medium mb-4 shadow-sm" data-id="element-792">
              <UsersIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-793" />
              Peer-Level Cohorts Only
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-794">
              Who This Is For
            </h2>
            <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-795">
              Designed for decision-makers shaping the future of AI in the
              enterprise.
            </p>
          </div>

          {/* Ideal Participant Profile */}
          <div className="max-w-5xl mx-auto mb-12" data-id="element-796">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-[#0099A1]/10" data-id="element-797">
              <div className="bg-gradient-to-r from-[#0099A1] to-[#006D73] px-8 py-6" data-id="element-798">
                <h3 className="text-2xl font-bold text-white text-center flex items-center justify-center gap-3" data-id="element-799">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-800">
                    <CheckIcon className="h-6 w-6 text-white" data-id="element-801" />
                  </div>
                  You're a Strong Fit If:
                </h3>
              </div>

              <div className="p-8 md:p-12" data-id="element-802">
                <div className="grid md:grid-cols-2 gap-8" data-id="element-803">
                  <div className="relative" data-id="element-804">
                    <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0099A1] to-[#006D73] rounded-full" data-id="element-805"></div>
                    <div className="space-y-6" data-id="element-806">
                      <div className="flex items-start gap-4" data-id="element-807">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-808">
                          <BrainCircuitIcon className="h-6 w-6 text-[#0099A1]" data-id="element-809" />
                        </div>
                        <div data-id="element-810">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-811">
                            Leading AI Strategy or Implementation
                          </p>
                          <p className="text-[#374047]/60" data-id="element-812">
                            Responsible for budget, tooling, or adoption
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-813">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-814">
                          <TargetIcon className="h-6 w-6 text-[#0099A1]" data-id="element-815" />
                        </div>
                        <div data-id="element-816">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-817">
                            Facing pressure to show ROI
                          </p>
                          <p className="text-[#374047]/60" data-id="element-818">
                            Need to move from pilot to production value
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="relative" data-id="element-819">
                    <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0099A1] to-[#006D73] rounded-full" data-id="element-820"></div>
                    <div className="space-y-6" data-id="element-821">
                      <div className="flex items-start gap-4" data-id="element-822">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-823">
                          <TrendingUpIcon className="h-6 w-6 text-[#0099A1]" data-id="element-824" />
                        </div>
                        <div data-id="element-825">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-826">
                            Senior Leadership Role
                          </p>
                          <p className="text-[#374047]/60" data-id="element-827">
                            VP, C-Level, or Head of Department
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-828">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-829">
                          <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-830" />
                        </div>
                        <div data-id="element-831">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-832">
                            Enterprise Scale
                          </p>
                          <p className="text-[#374047]/60" data-id="element-833">
                            Managing complex data/AI challenges
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-gray-200" data-id="element-834">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-835">
                    <div className="flex items-center gap-3" data-id="element-836">
                      <div className="w-10 h-10 bg-[#FF6B65]/10 rounded-full flex items-center justify-center" data-id="element-837">
                        <span className="text-2xl" data-id="element-838">⚡</span>
                      </div>
                      <p className="text-[#374047] font-medium" data-id="element-839">
                        <strong data-id="element-840">Typical profile:</strong> CAIO, CDO, CTO, or VP
                        of AI/Data
                      </p>
                    </div>
                    <Button variant="primary" to="/contact" data-id="element-841">
                      Check Your Fit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-optimized role selector */}
          <div className="overflow-x-auto pb-4 mb-6" data-id="element-842">
            <div className="flex flex-wrap justify-center gap-2 px-1" data-id="element-843">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'bg-white text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-844">
                  <span className="p-2 bg-white/80 rounded-full mb-2" data-id="element-845">
                    {persona.icon}
                  </span>
                  <span className="text-center whitespace-nowrap" data-id="element-846">
                    {persona.title}
                  </span>
                  {activePersona === persona.id && <div className="h-1 w-12 bg-[#FF6B65] rounded-full mt-2" data-id="element-847"></div>}
                </button>)}
            </div>
          </div>

          {/* Active Persona Value Proposition */}
          <div className="bg-white rounded-2xl shadow-xl border border-[#0099A1]/10 overflow-hidden mb-8" data-id="element-848">
            <div className="flex flex-col md:flex-row" data-id="element-849">
              {/* Hero Image for Active Persona */}
              <div className="relative h-48 md:h-auto md:w-2/5 flex-shrink-0" data-id="element-850">
                <img src={valuePropositions[activePersona].image} alt={valuePropositions[activePersona].title} className="w-full h-full object-cover object-center" data-id="element-851" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-852"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-853">
                  <h3 className="text-2xl font-bold text-white mb-1 drop-shadow-md" data-id="element-854">
                    {valuePropositions[activePersona].title}
                  </h3>
                  <p className="text-white/90 text-sm drop-shadow-md leading-snug" data-id="element-855">
                    {valuePropositions[activePersona].subtitle}
                  </p>
                </div>
              </div>

              <div className="p-6 md:p-8 md:w-3/5" data-id="element-856">
                {activePersona === 'caio' ? <>
                    <div className="mb-6" data-id="element-857">
                      <div className="flex items-center gap-3 mb-4" data-id="element-858">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-859">
                          Cohort capped at 20
                        </span>
                      </div>
                      <h4 className="text-2xl md:text-3xl font-bold text-[#374047] mb-3" data-id="element-860">
                        Build a board-ready AI strategy in 48 hours.
                      </h4>
                      <p className="text-lg text-[#374047]/80 mb-6 leading-relaxed" data-id="element-861">
                        {valuePropositions[activePersona].supportingLine}
                      </p>
                    </div>

                    <h5 className="font-bold text-[#374047] mb-4 text-lg" data-id="element-862">
                      Key Outcomes:
                    </h5>
                    <div className="space-y-4 mb-6" data-id="element-863">
                      {valuePropositions[activePersona].points.map((point, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/20 p-4 rounded-lg" data-id="element-864">
                            <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-865" />
                            <span className="ml-3 text-[#374047]" data-id="element-866">{point}</span>
                          </div>)}
                    </div>

                    <p className="text-sm text-[#374047]/60 mb-6 italic" data-id="element-867">
                      {valuePropositions[activePersona].riskRemover}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-868">
                      <Button variant="primary" to="/contact" className="flex-1 sm:flex-initial" data-id="element-869">
                        Book 15-Min Call
                      </Button>
                      <Button variant="secondary" to="/contact" className="flex-1 sm:flex-initial" data-id="element-870">
                        Check Eligibility
                      </Button>
                    </div>
                  </> : <>
                    <div className="border-l-4 border-[#FF6B65] pl-4 mb-6" data-id="element-871">
                      <p className="text-lg italic text-[#374047]" data-id="element-872">
                        "{personas.find(p => p.id === activePersona)?.hook}"
                      </p>
                    </div>
                    <p className="text-lg text-[#374047]/80 mb-6" data-id="element-873">
                      {personas.find(p => p.id === activePersona)?.value}
                    </p>
                    <h4 className="font-bold text-[#374047] mb-4" data-id="element-874">
                      Key Benefits:
                    </h4>
                    <div className="space-y-4 mb-8" data-id="element-875">
                      {valuePropositions[activePersona].points.map((point, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/20 p-3 rounded-lg" data-id="element-876">
                            <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-877" />
                            <span className="ml-3 text-[#374047]" data-id="element-878">{point}</span>
                          </div>)}
                    </div>
                    <div className="flex justify-start" data-id="element-879">
                      <Button variant="primary" to="/contact" data-id="element-880">
                        Request Your Invitation
                      </Button>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Build — Event Types Showcase */}
      <section className="py-16 bg-[#374047]" data-id="element-881">
        <div className="container mx-auto px-4 md:px-6" data-id="element-882">
          <div className="max-w-5xl mx-auto" data-id="element-883">
            <div className="text-center mb-10" data-id="element-884">
              <span className="text-xs font-bold tracking-[0.2em] text-[#0099A1] uppercase" data-id="element-885">
                Beyond the Retreat
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-3" data-id="element-886">
                Every Event Your AI Org Needs
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto" data-id="element-887">
                From Strategy Summits to Hackathons, from Customer Advisory
                Boards to Research Retreats — we design, produce, and execute
                them all.
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-888">
              {eventCategories.map((cat, idx) => <button key={idx} onClick={() => setActiveEventCategory(idx)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold tracking-wider transition-all duration-200 ${activeEventCategory === idx ? 'bg-white text-[#374047] shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`} data-id="element-889">
                  {cat.icon}
                  {cat.label}
                </button>)}
            </div>

            {/* Active Category Content */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-id="element-890">
              <div className="flex items-center gap-3 mb-4" data-id="element-891">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${eventCategories[activeEventCategory].color} flex items-center justify-center text-white`} data-id="element-892">
                  {eventCategories[activeEventCategory].icon}
                </div>
                <div data-id="element-893">
                  <div className="flex items-center gap-2" data-id="element-894">
                    <h3 className="text-xl font-bold text-white" data-id="element-895">
                      {eventCategories[activeEventCategory].label}
                    </h3>
                    <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-white/10 text-white/60" data-id="element-896">
                      {eventCategories[activeEventCategory].tagline}
                    </span>
                  </div>
                  <p className="text-white/50 text-sm mt-0.5" data-id="element-897">
                    {eventCategories[activeEventCategory].description}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3 mt-6" data-id="element-898">
                {eventCategories[activeEventCategory].events.map((event, idx) => <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors" data-id="element-899">
                      <h4 className="font-bold text-white text-sm mb-1" data-id="element-900">
                        {event.name}
                      </h4>
                      <p className="text-white/50 text-xs leading-relaxed" data-id="element-901">
                        {event.detail}
                      </p>
                    </div>)}
              </div>
            </div>

            <div className="text-center mt-8" data-id="element-902">
              <p className="text-white/40 text-sm mb-4" data-id="element-903">
                Don't see your event type? We've probably built it. Let's talk.
              </p>
              <Button variant="primary" to="/contact" data-id="element-904">
                Tell Us What You Need
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Package Details */}
      <section className="py-12 bg-white" data-id="element-905">
        <div className="container mx-auto px-4 md:px-6" data-id="element-906">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-907">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-908">
              How the Retreat Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-909">
              48 hours of focused work—venue and logistics handled so you can
              focus on building
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-910">
            <div data-id="element-911">
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-912">
                <CheckIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-913" />
                What's Provided
              </h3>
              <div className="space-y-3 mb-6" data-id="element-914">
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-915">
                  <div className="flex items-start gap-3" data-id="element-916">
                    <GraduationCapIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-917" />
                    <div data-id="element-918">
                      <p className="font-medium text-[#374047]" data-id="element-919">
                        Elite Faculty Sessions
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-920">
                        Top AI researchers and practitioners facilitate your
                        working sessions
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-921">
                  <div className="flex items-start gap-3" data-id="element-922">
                    <UsersIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-923" />
                    <div data-id="element-924">
                      <p className="font-medium text-[#374047]" data-id="element-925">
                        Peer Working Groups
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-926">
                        Collaborate with 20 other qualified AI leaders
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-927">
                  <div className="flex items-start gap-3" data-id="element-928">
                    <BedIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-929" />
                    <div data-id="element-930">
                      <p className="font-medium text-[#374047]" data-id="element-931">
                        Accommodations & Meals
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-932">
                        2 nights at a 5+ star resort, all meals and logistics
                        covered
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-933">
                  <div className="flex items-start gap-3" data-id="element-934">
                    <ClipboardCheckIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-935" />
                    <div data-id="element-936">
                      <p className="font-medium text-[#374047]" data-id="element-937">
                        Frameworks & Templates
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-938">
                        Battle-tested tools you customize for your company
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-939">
                <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-940" />
                Your Investment
              </h3>
              <div className="space-y-3" data-id="element-941">
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-942">
                  <div className="flex items-start gap-3" data-id="element-943">
                    <CalendarIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-944" />
                    <div data-id="element-945">
                      <p className="font-medium text-[#374047]" data-id="element-946">
                        Your flights to the destination
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-947">
                        Book your own travel (typically $300-600 roundtrip)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-948">
                  <div className="flex items-start gap-3" data-id="element-949">
                    <DollarSignIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-950" />
                    <div data-id="element-951">
                      <p className="font-medium text-[#374047]" data-id="element-952">
                        $250 refundable commitment deposit
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-953">
                        Returned upon attendance—ensures serious participants
                        only
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6" data-id="element-954">
                <Button variant="primary" to="/contact" className="w-full" data-id="element-955">
                  Apply for Next Retreat
                </Button>
              </div>
            </div>
            <div data-id="element-956">
              <Card className="h-full border-2 border-[#0099A1]/30" data-id="element-957">
                <CardHeader className="bg-[#0099A1] text-white p-4" data-id="element-958">
                  <h3 className="text-xl font-bold" data-id="element-959">Approval-Safe Package</h3>
                  <p className="text-sm text-white/90 mt-2" data-id="element-960">
                    Everything you need for compliance and internal approval
                  </p>
                </CardHeader>
                <CardBody className="p-8 pt-10" data-id="element-961">
                  <div className="space-y-6" data-id="element-962">
                    <div className="flex items-start gap-4" data-id="element-963">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-964">
                        <ClipboardCheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-965" />
                      </div>
                      <div data-id="element-966">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-967">
                          Official Invitation Letter
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-968">
                          Educational objectives, agenda, faculty credentials,
                          and hospitality breakdown for legal/compliance review
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-969">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-970">
                        <BookOpenIcon className="h-5 w-5 text-[#0099A1]" data-id="element-971" />
                      </div>
                      <div data-id="element-972">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-973">
                          CPD-Compliant Agenda
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-974">
                          Submit for Continuing Professional Development
                          credit—structured as executive education
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-975">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-976">
                        <DollarSignIcon className="h-5 w-5 text-[#0099A1]" data-id="element-977" />
                      </div>
                      <div data-id="element-978">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-979">
                          Transparent Value Disclosure
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-980">
                          Line-item breakdown of provided services for
                          compliance documentation
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-981">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-982">
                        <UserIcon className="h-5 w-5 text-[#0099A1]" data-id="element-983" />
                      </div>
                      <div data-id="element-984">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-985">
                          No-Sell Policy
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-986">
                          Written commitment: this is a working retreat, not a
                          sales pitch. No vendor presentations.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-[#F4E8D0]/30 rounded-lg" data-id="element-987">
                    <p className="text-sm text-[#374047] font-medium" data-id="element-988">
                      <strong data-id="element-989">Why this matters:</strong> Many companies have
                      strict gifting policies. Our compliance package makes
                      approval straightforward.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Accommodations */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-990">
        <div className="container mx-auto px-4 md:px-6" data-id="element-991">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-992">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-993">
              Your Luxury Accommodations
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-994">
              Enjoy a complimentary 2-night stay at iconic 5+ star resorts,
              including Palace Resorts, El Dorado, Meliá, SLS, Nobu, Hard Rock &
              more.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start mb-16" data-id="element-995">
            <div data-id="element-996">
              <div className="flex items-center mb-6" data-id="element-997">
                <BedIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-998" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-999">
                  Luxurious Oceanfront Experience
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1000">
                Experience the ultimate comfort in meticulously designed rooms
                featuring ocean views, spa amenities, and premium services.
              </p>
              <div className="space-y-3 mb-8" data-id="element-1001">
                <div className="flex items-start" data-id="element-1002">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1003" />
                  <span className="ml-3 text-[#374047]" data-id="element-1004">
                    Spacious suites with breathtaking Caribbean views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1005">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1006" />
                  <span className="ml-3 text-[#374047]" data-id="element-1007">
                    Premium spa and wellness amenities
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1008">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1009" />
                  <span className="ml-3 text-[#374047]" data-id="element-1010">
                    Private balconies with panoramic views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1011">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1012" />
                  <span className="ml-3 text-[#374047]" data-id="element-1013">
                    Premium amenities and 24-hour room service
                  </span>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-1014">
                <UtensilsIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1015" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1016">
                  World-Class Dining
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1017">
                Indulge in exceptional culinary experiences across multiple
                restaurants offering international cuisines.
              </p>
              <Button variant="primary" to="/contact" data-id="element-1018">
                Apply for Your Stay
              </Button>
            </div>
            <div data-id="element-1019">
              <div className="grid grid-cols-2 gap-4" data-id="element-1020">
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-1021">
                  <img src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80" alt="Luxury Resort Suite" className="w-full h-64 object-cover" data-id="element-1022" />
                </div>
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-1023">
                  <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80" alt="Luxury Resort Pool" className="w-full h-64 object-cover" data-id="element-1024" />
                </div>
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-1025">
                  <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80" alt="Fine Dining Experience" className="w-full h-64 object-cover" data-id="element-1026" />
                </div>
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-1027">
                  <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80" alt="Luxury Beach Resort" className="w-full h-64 object-cover" data-id="element-1028" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6-Star Resort Activities */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1029">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1030">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1031">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1032">
              6-Star Resort Entertainment & Activities
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1033">
              Work hard, play harder with these amazing experiences included in
              your stay
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1034">
            {resortActivities.map((activity, index) => <div key={index} className="bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-xl p-6 text-white shadow-lg transform transition-transform hover:scale-105" data-id="element-1035">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4" data-id="element-1036">
                  {activity.icon}
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1037">{activity.title}</h3>
                <p className="text-white/80" data-id="element-1038">{activity.description}</p>
              </div>)}
          </div>
          <div className="max-w-3xl mx-auto text-center mt-12" data-id="element-1039">
            <p className="text-[#374047]/70 mb-6" data-id="element-1040">
              All activities and entertainment are included in your
              complimentary stay
            </p>
            <Button variant="primary" to="/contact" data-id="element-1041">
              Check Eligibility
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden" style={{
      background: 'linear-gradient(to right, #0099A1, #006D73)'
    }} data-id="element-1042">
        <div className="absolute inset-0 opacity-20" data-id="element-1043">
          <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80" alt="Luxury Resort Background" className="w-full h-full object-cover" data-id="element-1044" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1045">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1046">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/30" data-id="element-1047">
              <BrainCircuitIcon className="h-4 w-4 mr-2" data-id="element-1048" />
              Next Cohort: Limited to 20 Qualified Leaders
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1049">
              Ready to Define Your AI Strategy?
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-1050">
              Apply now for a 15-minute fit check. We'll review your eligibility
              and answer any compliance questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6" data-id="element-1051">
              <Button variant="primary" size="lg" to="/contact" data-id="element-1052">
                Check Eligibility
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20" data-id="element-1053">
                Book 15-Min Call
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/80" data-id="element-1054">
              <div className="flex items-center gap-2" data-id="element-1055">
                <CheckIcon className="h-4 w-4" data-id="element-1056" />
                <span data-id="element-1057">Approval-safe package provided</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1058">
                <CheckIcon className="h-4 w-4" data-id="element-1059" />
                <span data-id="element-1060">No vendor pitch policy</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1061">
                <CheckIcon className="h-4 w-4" data-id="element-1062" />
                <span data-id="element-1063">Peer-level cohorts only</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Booking Embed */}
      <BookingEmbed data-id="element-1064" />
      <CtaSection data-id="element-1065" />
    </div>;
}